<template>
  <v-container class="pa-0">
    <!-- spinner that shows if fabrics or fabric details are loading -->
    <loading-overlay :is-loading="isLoading" />

    <!-- the fabric cards -->
    <v-row v-if="fabrics && fabrics.length">
      <v-col
        v-for="(_fabric, index) in fabrics"
        :key="index"
        class="d-flex child-flex"
        sm="6"
        lg="3"
        cols="12"
      >
        <card
          v-if="_fabric"
          :id="_fabric.fabric_uuid"
          :label="_fabric.discount ? _fabric.discount.label : $t('components.fabricPicker.labels.bargain')"
          :title="_fabric.name"
          :info-dialog-title="_fabric.name"
          :sub-title="cardSubTitle(_fabric)"
          :img-src="_fabric.rendered_image"
          :multiple="multiple"
          :sale-label-spotlight="!_fabric.discount ? '' : _fabric.discount.label"
          :selected="isSelectedFabric(_fabric.fabric_uuid)"
          @onClick="pickFabric"
          @onInfoClick="onCardInfoClick"
        />
      </v-col>
    </v-row>
    <h3 v-else>
      {{ noFabricsAvailableText }}
    </h3>
    <!-- division between fabric cards and pagination -->
    <v-divider class="my-10" />

    <v-row>
      <v-col
        cols="12"
        md="6"
      >
        <fabric-picker-card v-if="fabric" />
      </v-col>
      <v-col
        cols="12"
        md="6"
      >
        <div
          class="confirmButtonsBlock"
          style="justify-content: flex-end"
        >
          <v-btn
            class="primary"
            right
            :disabled="!fabric"
            @click="next"
          >
            Next step
            <v-icon class="white--text pl-5">
              mdi mdi-chevron-right
            </v-icon>
          </v-btn>
        </div>
      </v-col>
    </v-row>

    <information-dialog
      :fabric-picker="true"
      :fabric-details="fabricDetails"
      :show-dialog="showFabricInfoDialog"
      :title-dialog="$t('components.inform.fabricTitle')"
      fabric
      dialog-action
      @closeDialog="showFabricInfoDialog = false"
      @pickFabric="fabricInfoDialogPickFabric"
    />
  </v-container>
</template>

<script>
import Card from 'vue-flow-generator/src/components/Widgets/CheckboxCard'
import InformationDialog from '@/components/Dialogs/InformationDialog'
import FabricPickerCard from '@/components/Cards/FabricPickerCard'
import LoadingOverlay from '@/components/Overlays/LoadingOverlay'
import { FABRICS, LININGS } from '@/constants'
import { mapGetters } from 'vuex'

export default {
  name: 'FabricPicker',
  components: {
    Card,
    InformationDialog,
    LoadingOverlay,
    FabricPickerCard
  },
  props: {
    multiple: {
      type: Boolean,
      default: false,
      description: 'If multiple fabrics can be selected'
    },
    maxSelectionCount: {
      type: Number,
      default: 5,
      description:
        'The maximum amount of fabrics which is allowed to be selected'
    },
    fabricType: {
      type: String,
      default: FABRICS,
      description: 'The fabric type, can be either FABRICS or LININGS'
    },
    page: {
      type: Number,
      default: 1
    },
    fabricUuid: {
      type: String,
      default: null
    }
  },
  data: () => ({
    pageSize: 100,
    showTooManyFabricsDialog: false,
    showFabricInfoDialog: false,
    selectedFabricUuid: null
  }),
  computed: {
    ...mapGetters({
      orderFabric: 'fabrics/getFabricDetails'
    }),
    isLoading() {
      return this.$wait.is('isLoadingFabrics')
    },
    fabric() {
      // note that this component can be used for main fabrics only
      return !!Object.keys(this.selectedFabrics[0]).length > 0
    },
    fabrics() {
      return this.$store.getters[`${this.fabricType}/getFabrics`]
    },

    fabricDetails() {
      //temporary solution for mock two images for each curtain type
      //will be deleted when images will be come from API
      const details =
        this.$store.getters[`${this.fabricType}/getFabricDetails`][
          this.selectedFabricUuid
        ] || {}

      // Washing images are not defaulted anymore and only shown if the API
      // supplies them:
      // details.wash_property = 'inynormalwash30'
      // details.bleach_property = 'inydontbleach'
      // details.dry_property = 'inydontdry'
      // details.clear_property = 'inyclearp'

      //description block
      // will be deleted when description will be come from API
      details.description = this.$t('components.inform.fabricDescription')
      return { ...details }
    },
    totalFabrics() {
      return this.$store.getters[`${this.fabricType}/getCount`]
    },
    queryParams() {
      return {
        ...this.$store.getters[`${this.fabricType}/filters/getQueryParams`],
        page: this.page,
        page_size: this.pageSize
      }
    },
    selectedFabrics() {
      if (this.multiple) {
        return this.$store.getters['order/getFabrics']
      } else {
        // for a single fabric, check if fabricType is LININGS or FABRICS
        const selectedFabric =
          this.fabricType === LININGS
            ? this.$store.getters['order/getLining']
            : this.$store.getters['order/getFabric']
        return selectedFabric ? [selectedFabric] : []
      }
    },
    tooManyFabricsDialogText() {
      return this.$i18n.t('components.fabricPicker.tooManyFabricsDialogText', {
        maxSelectionCount: this.maxSelectionCount
      })
    },
    noFabricsAvailableText() {
      if (!this.isLoading && (!this.fabrics || !this.fabrics.length)) {
        return this.$i18n.t('components.fabricPicker.noFabricsAvailableText')
      }
      return ''
    },
    isSelectionAllowed() {
      return this.selectedFabrics.length < this.maxSelectionCount
    }
  },
  watch: {
    $route: 'fetchData',
    fabricUuid(id) {
      this.pickFabric(id)
    }
  },
  mounted() {
    this.fetchData()
  },
  methods: {
    async fetchData() {
      // prepare query parameters
      const queryParams = this.queryParams
      this.$wait.start('isLoadingFabrics')
      await this.$store.dispatch(`${this.fabricType}/loadFabrics`, {
        queryParams
      })
      this.$wait.end('isLoadingFabrics')
    },
    async fetchDetails() {
      // fetch fabric details for selectedFabricUuid
      const urlParams = { fabricUuid: this.selectedFabricUuid }
      this.$wait.start('isLoadingFabrics')
      await this.$store.dispatch(`${this.fabricType}/loadFabricDetails`, {
        urlParams
      })
      this.$wait.end('isLoadingFabrics')
    },
    isSelectedFabric(fabricUuid) {
      // look up a complete fabric in selectedFabrics by fabricUuid
      return this.selectedFabrics.some((selectedFabric) => {
        return selectedFabric.fabric_uuid === fabricUuid
      })
    },
    async pickMultiSelectFabric(fabricUuid) {
      if (this.isSelectedFabric(fabricUuid)) {
        // fabric is already selected on click, delete from selection
        await this.$store.dispatch('order/deleteFabric', { fabricUuid })
      } else {
        // fabric not selected yet, add to selection if allowed
        if (this.isSelectionAllowed) {
          this.selectedFabricUuid = fabricUuid
          await this.fetchDetails()
          await this.$store.dispatch('order/addFabric', {
            fabricDetails: this.fabricDetails
          })
        } else {
          this.showTooManyFabricsDialog = true
        }
      }
    },
    async pickSingleSelectFabric(fabricUuid) {
      this.selectedFabricUuid = fabricUuid
      await this.fetchDetails()
      // for a single fabric, check if fabricType is LININGS or FABRICS
      const setter =
        this.fabricType === LININGS ? 'order/setLining' : 'order/setFabric'
      await this.$store.dispatch(setter, { fabricDetails: this.fabricDetails })
    },
    async pickFabric(fabricUuid) {
      if (this.multiple) {
        await this.pickMultiSelectFabric(fabricUuid)
      } else {
        await this.pickSingleSelectFabric(fabricUuid)
      }
      this.$emit('pickFabric', fabricUuid)
    },
    async onCardInfoClick(fabricUuid) {
      this.showFabricInfoDialog = true
      this.selectedFabricUuid = fabricUuid
      await this.fetchDetails()
    },
    cardSubTitle(fabric) {
      if (!!fabric.discount && !!fabric.discount.origin_price && !!fabric.discount.discount_price) {
        return `<p class="text-right mb-0">
            ${this.$i18n.t('components.fabricPicker.cardSubTitleWithPrice')}
            <br />
            <strong style="text-decoration: line-through; text-decoration-color:red;" class="text-h3 font-weight-bold primary--text">
              $${fabric.discount.origin_price}
            </strong>
            <br/>
            <strong class="text-h3 font-weight-bold" style="color: red"> $${fabric.discount.discount_price}</strong>
          </p>`
      } else if (!!fabric.price) {
        return `<p class="text-right mb-0">
            ${this.$i18n.t('components.fabricPicker.cardSubTitleWithPrice')}
            <br />
            <strong class="text-h3 font-weight-bold primary--text">
              $${fabric.price}
            </strong>
          </p>`
      } else {
        return `<p class="text-right mb-0">
            ${this.$i18n.t('components.fabricPicker.cardSubTitlePriceMissing')}
            </p>`
      }
    },
    async fabricInfoDialogPickFabric(fabricUuid) {
      await this.pickFabric(fabricUuid)
      this.showFabricInfoDialog = false
    },
    next() {
      this.$emit('next', this.selectedFabricUuid)
    }
  }
}
</script>

<style lang="scss">
.progress {
  margin-top: 30px;
  margin-left: 50%;
}

.v-pagination {
  li button {
    font-size: 12px;
  }
}

.v-dialog {
  border-radius: 4px;

  table tr > td:first-child {
    padding-right: 30px;
  }
}

.v-dialog .v-card {
  font-weight: 600;
  font-size: 17px !important;
}

.fill {
  min-height: 85vh;

  h3 {
    text-align: center;
    padding-top: 40vh;
  }
}
</style>
